import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import SearchInput from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, MapPin, Edit, Trash2, Loader2, CheckCircle, XCircle, Truck, Clock, RefreshCw } from "lucide-react";

const WORKING_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIME_SLOTS = ["09:00-12:00", "12:00-15:00", "15:00-18:00", "18:00-21:00"];

// Extracted LocationForm component to prevent cursor jumping issue
const LocationForm = ({ 
  onSubmit, 
  submitText, 
  formData, 
  setFormData, 
  sellers, 
  selectedLocation,
  pincodeLoading,
  returnPincodeLoading,
  lookupPincode 
}) => (
  <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
    {/* Seller Selection */}
    <div className="space-y-2">
      <Label>Seller Organization *</Label>
      <Select
        value={formData.seller_id}
        onValueChange={(value) => setFormData({ ...formData, seller_id: value })}
        required
        disabled={!!selectedLocation}
      >
        <SelectTrigger data-testid="pickup-seller-select">
          <SelectValue placeholder="Select seller" />
        </SelectTrigger>
        <SelectContent>
          {sellers.map((seller) => (
            <SelectItem key={seller.seller_id} value={seller.seller_id}>
              {seller.brand_name || seller.company_registered_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Facility Details */}
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="facility_name">Facility Name *</Label>
        <Input
          id="facility_name"
          data-testid="facility-name-input"
          value={formData.facility_name}
          onChange={(e) => setFormData({ ...formData, facility_name: e.target.value })}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact_person_name">Contact Person Name *</Label>
        <Input
          id="contact_person_name"
          data-testid="contact-person-input"
          value={formData.contact_person_name}
          onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })}
          required
        />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="contact_phone">Contact Phone *</Label>
        <Input
          id="contact_phone"
          data-testid="contact-phone-input"
          value={formData.contact_phone}
          onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
          maxLength={10}
          inputMode="numeric"
          placeholder="10-digit number"
          required
        />
        {formData.contact_phone && formData.contact_phone.length !== 10 && (
          <p className="text-xs text-red-600">Enter a valid 10-digit phone number (required for the Delhivery manifest).</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact_email">Contact Email *</Label>
        <Input
          id="contact_email"
          data-testid="contact-email-input"
          type="email"
          value={formData.contact_email}
          onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
          required
        />
      </div>
    </div>

    {/* Address */}
    <div className="space-y-2">
      <Label htmlFor="address_line">Address Line *</Label>
      <Input
        id="address_line"
        data-testid="address-line-input"
        value={formData.address_line}
        onChange={(e) => setFormData({ ...formData, address_line: e.target.value })}
        required
      />
    </div>

    <div className="grid grid-cols-3 gap-4">
      <div className="space-y-2">
        <Label htmlFor="pincode">Pincode *</Label>
        <div className="flex gap-2">
          <Input
            id="pincode"
            data-testid="pincode-input"
            value={formData.pincode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 6);
              setFormData({ ...formData, pincode: value });
              if (value.length === 6) {
                lookupPincode(value);
              }
            }}
            maxLength={6}
            inputMode="numeric"
            required
          />
          {pincodeLoading && <Loader2 className="w-4 h-4 animate-spin mt-2" />}
        </div>
        {formData.pincode && formData.pincode.length !== 6 && (
          <p className="text-xs text-red-600">Pincode must be 6 digits.</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="city">City *</Label>
        <Input
          id="city"
          data-testid="city-input"
          value={formData.city}
          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="state">State *</Label>
        <Input
          id="state"
          data-testid="state-input"
          value={formData.state}
          onChange={(e) => setFormData({ ...formData, state: e.target.value })}
          required
        />
      </div>
    </div>

    {/* Return Address Section */}
    <div className="space-y-4 p-4 border rounded-lg bg-slate-50">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="has_return_address"
          checked={formData.has_return_address}
          onCheckedChange={(checked) => setFormData({ ...formData, has_return_address: checked })}
        />
        <Label htmlFor="has_return_address" className="text-sm font-medium">
          Return address is different
        </Label>
      </div>

      {formData.has_return_address && (
        <div className="space-y-4 pl-6 border-l-2 border-slate-300">
          <div className="space-y-2">
            <Label htmlFor="return_address_line">Return Address Line *</Label>
            <Input
              id="return_address_line"
              data-testid="return-address-line-input"
              value={formData.return_address?.address_line || ""}
              onChange={(e) => setFormData({
                ...formData,
                return_address: { ...formData.return_address, address_line: e.target.value }
              })}
              required={formData.has_return_address}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="return_pincode">Pincode *</Label>
              <div className="flex gap-2">
                <Input
                  id="return_pincode"
                  data-testid="return-pincode-input"
                  value={formData.return_address?.pincode || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({
                      ...formData,
                      return_address: { ...formData.return_address, pincode: value }
                    });
                    if (value.length === 6) {
                      lookupPincode(value, true);
                    }
                  }}
                  maxLength={6}
                  required={formData.has_return_address}
                />
                {returnPincodeLoading && <Loader2 className="w-4 h-4 animate-spin mt-2" />}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="return_city">City *</Label>
              <Input
                id="return_city"
                data-testid="return-city-input"
                value={formData.return_address?.city || ""}
                onChange={(e) => setFormData({
                  ...formData,
                  return_address: { ...formData.return_address, city: e.target.value }
                })}
                required={formData.has_return_address}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="return_state">State *</Label>
              <Input
                id="return_state"
                data-testid="return-state-input"
                value={formData.return_address?.state || ""}
                onChange={(e) => setFormData({
                  ...formData,
                  return_address: { ...formData.return_address, state: e.target.value }
                })}
                required={formData.has_return_address}
              />
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Operating Hours */}
    <div className="space-y-4">
      <Label>Working Days & Hours *</Label>
      <div className="grid grid-cols-2 gap-2">
        {WORKING_DAYS.map((day) => (
          <div key={day} className="flex items-center space-x-2">
            <Checkbox
              id={day}
              checked={formData.working_days?.includes(day)}
              onCheckedChange={(checked) => {
                const currentDays = formData.working_days || [];
                const newDays = checked
                  ? [...currentDays, day]
                  : currentDays.filter((d) => d !== day);
                setFormData({ ...formData, working_days: newDays });
              }}
            />
            <Label htmlFor={day} className="text-sm">{day}</Label>
          </div>
        ))}
      </div>
    </div>

    <div className="space-y-2">
      <Label>Operating Hours *</Label>
      <Select
        value={formData.operating_hours}
        onValueChange={(value) => setFormData({ ...formData, operating_hours: value })}
        required
      >
        <SelectTrigger data-testid="operating-hours-select">
          <SelectValue placeholder="Select time slot" />
        </SelectTrigger>
        <SelectContent>
          {TIME_SLOTS.map((slot) => (
            <SelectItem key={slot} value={slot}>
              {slot}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <Button type="submit" data-testid="pickup-form-submit-button" className="w-full" style={{backgroundColor: '#00A699'}}>
      {submitText}
    </Button>
  </form>
);

const PickupLocations = ({ user, onLogout }) => {
  const [locations, setLocations] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [returnPincodeLoading, setReturnPincodeLoading] = useState(false);
  const [shippingPartners, setShippingPartners] = useState([]);
  const [registering, setRegistering] = useState({});
  const [b2cPartners, setB2cPartners] = useState([]);
  const [b2cRetrying, setB2cRetrying] = useState({});
  const [backfilling, setBackfilling] = useState(false);

  const [formData, setFormData] = useState({
    seller_id: "",
    facility_name: "",
    contact_person_name: "",
    pickup_contact: "",
    email: "",
    address_line: "",
    pincode: "",
    city: "",
    state: "",
    default_pickup_slot: "",
    working_days: [],
    return_address_same: true,
    return_address: {
      address_line: "",
      pincode: "",
      city: "",
      state: "",
    },
  });

  useEffect(() => {
    fetchLocations();
    fetchSellers();
    fetchShippingPartners();
    fetchB2cPartners();
  }, []);

  const fetchLocations = async (query = "") => {
    try {
      const url = query ? `${API}/pickup-locations/?q=${encodeURIComponent(query)}` : `${API}/pickup-locations/`;
      const response = await axios.get(url);
      setLocations(response.data);
    } catch (error) {
      toast.error("Failed to fetch pickup locations");
    } finally {
      setLoading(false);
    }
  };

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/sellers-org/`);
      setSellers(response.data);
    } catch (error) {
      console.error("Failed to fetch sellers", error);
    }
  };

  const fetchShippingPartners = async () => {
    try {
      const response = await axios.get(`${API}/b2b-shipping-partner/active`);
      setShippingPartners(response.data);
    } catch (error) {
      console.error("Failed to fetch shipping partners", error);
    }
  };

  // B2C aggregators (Shiprocket/iThink). Registration is AUTOMATIC on create; this
  // column is a status indicator + an admin Sync/retry. Only enabled partners are
  // shown (only they get registered). Admin-only page — aggregator names are fine here.
  const fetchB2cPartners = async () => {
    try {
      const response = await axios.get(`${API}/b2c/admin/partners`);
      setB2cPartners((response.data || []).filter((p) => p.enabled));
    } catch (error) {
      // B2C partner view is optional here; ignore (e.g. admin without the perm).
    }
  };

  const handleRetryB2c = async (locationId) => {
    setB2cRetrying((prev) => ({ ...prev, [locationId]: true }));
    try {
      await axios.post(`${API}/pickup-locations/${locationId}/register-b2c`);
      toast.success("B2C aggregator registration triggered");
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to register in B2C aggregators");
    } finally {
      setB2cRetrying((prev) => ({ ...prev, [locationId]: false }));
    }
  };

  const handleBackfillB2c = async () => {
    if (!window.confirm("Register all active pickup locations in the enabled B2C aggregators? This runs in the background.")) return;
    setBackfilling(true);
    try {
      await axios.post(`${API}/pickup-locations/backfill-b2c`);
      toast.success("B2C backfill started in the background — statuses will update shortly.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start B2C backfill");
    } finally {
      setBackfilling(false);
    }
  };

  const handleRegisterInPartner = async (locationId, partnerId, partnerName) => {
    const key = `${locationId}-${partnerId}`;
    setRegistering((prev) => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API}/pickup-locations/${locationId}/register-partner/${partnerId}`);
      toast.success(`Warehouse registered in ${partnerName}`);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to register in ${partnerName}`);
    } finally {
      setRegistering((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSearch = (query) => {
    fetchLocations(query);
  };

  const lookupPincode = async (pincode, isReturn = false) => {
    if (pincode.length !== 6) return;

    if (isReturn) {
      setReturnPincodeLoading(true);
    } else {
      setPincodeLoading(true);
    }

    try {
      const response = await axios.get(`${API}/pickup-locations/pincode/${pincode}`);
      if (isReturn) {
        setFormData((prev) => ({
          ...prev,
          return_address: {
            ...prev.return_address,
            pincode: pincode,
            city: response.data.city,
            state: response.data.state,
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          pincode: pincode,
          city: response.data.city,
          state: response.data.state,
        }));
      }
      toast.success("Pincode verified!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid pincode");
    } finally {
      if (isReturn) {
        setReturnPincodeLoading(false);
      } else {
        setPincodeLoading(false);
      }
    }
  };

  const handleWorkingDayToggle = (day) => {
    const currentDays = formData.working_days;
    if (currentDays.includes(day)) {
      setFormData({ ...formData, working_days: currentDays.filter((d) => d !== day) });
    } else {
      setFormData({ ...formData, working_days: [...currentDays, day] });
    }
  };

  const formatErrorMessage = (error) => {
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      // Handle Pydantic validation errors
      return detail.map(err => err.msg || JSON.stringify(err)).join(", ");
    }
    return detail || "An error occurred";
  };

  const handleCreateLocation = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(formData.contact_phone || "")) { toast.error("Enter a valid 10-digit contact phone."); return; }
    if (!/^\d{6}$/.test(formData.pincode || "")) { toast.error("Pincode must be 6 digits."); return; }
    try {
      // pickup_contact is the field the Delhivery manifest reads; keep it in sync.
      const payload = { ...formData, pickup_contact: formData.contact_phone };
      if (formData.return_address_same) {
        payload.return_address = null;
      }
      await axios.post(`${API}/pickup-locations/`, payload);
      toast.success("Pickup location created successfully!");
      setShowCreateDialog(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleUpdateLocation = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(formData.contact_phone || "")) { toast.error("Enter a valid 10-digit contact phone."); return; }
    if (!/^\d{6}$/.test(formData.pincode || "")) { toast.error("Pincode must be 6 digits."); return; }
    try {
      const payload = { ...formData, pickup_contact: formData.contact_phone };
      delete payload.seller_id;
      if (formData.return_address_same) {
        payload.return_address = null;
      }
      await axios.put(`${API}/pickup-locations/${selectedLocation.id}`, payload);
      toast.success("Pickup location updated successfully!");
      setShowEditDialog(false);
      setSelectedLocation(null);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleToggleStatus = async (locationId, currentStatus) => {
    const action = currentStatus ? "disable" : "enable";
    if (!window.confirm(`Are you sure you want to ${action} this pickup location?`)) return;

    try {
      const response = await axios.patch(`${API}/pickup-locations/${locationId}/toggle-status`);
      toast.success(response.data.message);
      fetchLocations();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const resetForm = () => {
    setFormData({
      seller_id: "",
      facility_name: "",
      contact_person_name: "",
      pickup_contact: "",
      email: "",
      address_line: "",
      pincode: "",
      city: "",
      state: "",
      default_pickup_slot: "",
      working_days: [],
      return_address_same: true,
      return_address: {
        address_line: "",
        pincode: "",
        city: "",
        state: "",
      },
    });
  };

  const openEditDialog = (location) => {
    setSelectedLocation(location);
    setFormData({
      seller_id: location.seller_id,
      facility_name: location.facility_name,
      contact_person_name: location.contact_person_name || "",
      pickup_contact: location.pickup_contact,
      contact_phone: location.contact_phone || location.pickup_contact || "",
      email: location.email || "",
      address_line: location.address_line,
      pincode: location.pincode,
      city: location.city,
      state: location.state,
      default_pickup_slot: location.default_pickup_slot,
      working_days: location.working_days || [],
      return_address_same: location.return_address_same,
      return_address: location.return_address || {
        address_line: "",
        pincode: "",
        city: "",
        state: "",
      },
    });
    setShowEditDialog(true);
  };

  const getSeller = (sellerId) => {
    const seller = sellers.find((s) => s.seller_id === sellerId);
    return seller ? (seller.brand_name || seller.company_registered_name) : "Unknown Seller";
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">Pickup Locations</h1>
            <p className="text-slate-600">Manage pickup locations for sellers</p>
          </div>
          <div className="flex gap-4 items-center">
            <SearchInput
              onSearch={handleSearch}
              placeholder="Search locations..."
            />
            {b2cPartners.length > 0 && (
              <Button
                data-testid="b2c-backfill-button"
                variant="outline"
                onClick={handleBackfillB2c}
                disabled={backfilling}
                title="Register existing pickup locations in the enabled B2C aggregators"
              >
                {backfilling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Backfill B2C
              </Button>
            )}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button data-testid="create-pickup-location-button" style={{backgroundColor: '#00A699'}} onClick={() => resetForm()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Pickup Location
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Add Pickup Location</DialogTitle>
                <DialogDescription>Create a new pickup location for a seller</DialogDescription>
              </DialogHeader>
              <LocationForm 
                onSubmit={handleCreateLocation} 
                submitText="Create Location"
                formData={formData}
                setFormData={setFormData}
                sellers={sellers}
                selectedLocation={selectedLocation}
                pincodeLoading={pincodeLoading}
                returnPincodeLoading={returnPincodeLoading}
                lookupPincode={lookupPincode}
              />
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-slate-200 rounded w-32"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-slate-200 rounded w-full"></div>
                    <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : locations.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <MapPin className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No pickup locations yet</h3>
              <p className="text-slate-500 mb-6">Add your first pickup location to get started</p>
              <Button data-testid="empty-create-pickup-button" style={{backgroundColor: '#00A699'}} onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Pickup Location
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Location ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Facility</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Seller</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Address</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Working Days</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Slot</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Added By</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Shipping Partners</th>
                    {b2cPartners.length > 0 && (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">B2C Integration</th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {locations.map((location) => (
                    <tr key={location.id} data-testid={`pickup-location-row-${location.id}`} className="hover:bg-slate-50">
                      <td className="px-4 py-3 align-middle">
                        <span className="text-sm font-mono text-blue-600 font-medium">{location.location_id}</span>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className="text-sm font-medium text-slate-900">{location.facility_name}</span>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className="text-sm text-slate-700">{getSeller(location.seller_id)}</span>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div>
                          <p className="text-sm text-slate-900">{location.contact_person_name}</p>
                          <p className="text-xs text-slate-500">{location.contact_phone}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="max-w-xs">
                          <p className="text-sm text-slate-700 truncate">{location.address_line}</p>
                          <p className="text-xs text-slate-500">{location.city}, {location.state} - {location.pincode}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap text-right">
                        <span className="text-sm text-slate-700">{location.working_days?.length || 0} days</span>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <span className="text-sm text-slate-700">{location.operating_hours}</span>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <div>
                          <p className="text-sm text-slate-700">{location.created_by?.user_name || '-'}</p>
                          <p className="text-xs text-slate-500">{location.created_by?.user_email || ''}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <Badge className={location.is_active ? "bg-green-100 text-green-800 border-0" : "bg-red-100 text-red-800 border-0"}>
                          {location.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          {shippingPartners.map((partner) => {
                            const reg = location.partner_registrations?.[partner.id];
                            const isCreated = reg?.status === "created";
                            const isFailed = reg?.status === "failed";
                            const regKey = `${location.id}-${partner.id}`;
                            const isLoading = registering[regKey];
                            return (
                              <div key={partner.id} className="flex items-center gap-1.5">
                                {isCreated ? (
                                  <Badge className="bg-green-100 text-green-700 border-0 text-xs gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    {partner.name}
                                  </Badge>
                                ) : (
                                  <Button
                                    data-testid={`register-${location.id}-${partner.id}`}
                                    size="sm"
                                    variant="outline"
                                    className={`h-6 text-xs gap-1 ${isFailed ? "border-red-300 text-red-600 hover:bg-red-50" : "border-teal-300 text-teal-700 hover:bg-teal-50"}`}
                                    onClick={() => handleRegisterInPartner(location.id, partner.id, partner.name)}
                                    disabled={isLoading}
                                  >
                                    {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Truck className="w-3 h-3" />}
                                    {isFailed ? `Retry ${partner.name}` : `Create in ${partner.name}`}
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                          {shippingPartners.length === 0 && <span className="text-xs text-slate-400">-</span>}
                        </div>
                      </td>
                      {b2cPartners.length > 0 && (
                        <td className="px-4 py-3 align-middle whitespace-nowrap" data-testid={`b2c-integration-${location.id}`}>
                          <div className="flex flex-col gap-1">
                            {b2cPartners.map((p) => {
                              const reg = location.partner_registrations?.[p.id];
                              const status = reg?.status;
                              const label = p.display_name || p.name;
                              return (
                                <div key={p.id}>
                                  {status === "created" ? (
                                    <Badge className="bg-green-100 text-green-700 border-0 text-xs gap-1">
                                      <CheckCircle className="w-3 h-3" /> {label} · integrated
                                    </Badge>
                                  ) : status === "pending" ? (
                                    <Badge className="bg-amber-100 text-amber-700 border-0 text-xs gap-1" title={reg?.approval_status || "awaiting approval"}>
                                      <Clock className="w-3 h-3" /> {label} · pending
                                    </Badge>
                                  ) : status === "failed" ? (
                                    <Badge className="bg-red-100 text-red-700 border-0 text-xs gap-1" title={reg?.last_error || "registration failed"}>
                                      <XCircle className="w-3 h-3" /> {label} · failed
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-slate-100 text-slate-500 border-0 text-xs">{label} · not registered</Badge>
                                  )}
                                </div>
                              );
                            })}
                            <Button
                              data-testid={`register-b2c-${location.id}`}
                              size="sm"
                              variant="outline"
                              className="h-6 text-xs gap-1 border-teal-300 text-teal-700 hover:bg-teal-50 w-fit"
                              onClick={() => handleRetryB2c(location.id)}
                              disabled={b2cRetrying[location.id]}
                              title="Re-register this location in the enabled B2C aggregators"
                            >
                              {b2cRetrying[location.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                              Sync
                            </Button>
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <div className="flex gap-2">
                          <Button
                            data-testid={`edit-pickup-button-${location.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(location)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`toggle-pickup-button-${location.id}`}
                            size="sm"
                            variant="ghost"
                            className={location.is_active ? "text-orange-600 hover:text-orange-700" : "text-green-600 hover:text-green-700"}
                            onClick={() => handleToggleStatus(location.id, location.is_active)}
                            title={location.is_active ? "Disable" : "Enable"}
                          >
                            {location.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Pickup Location</DialogTitle>
            <DialogDescription>Update pickup location details</DialogDescription>
          </DialogHeader>
          <LocationForm 
            onSubmit={handleUpdateLocation} 
            submitText="Update Location"
            formData={formData}
            setFormData={setFormData}
            sellers={sellers}
            selectedLocation={selectedLocation}
            pincodeLoading={pincodeLoading}
            returnPincodeLoading={returnPincodeLoading}
            lookupPincode={lookupPincode}
          />
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default PickupLocations;
